import type { ControlMasterDataSource, Steuerbox, SteuerboxType, Steuerpfad, TechnischeRessource } from '../../../types/network';
import { fmt } from '../../../utils/numberFormat';

// Labels and small building blocks of the §14a control master data (migration 0060,
// doc/Architektur/STEUERUNG.md 4.1) shared by the Steuerbox and resource editors.

export const SOURCE_LABELS: Record<ControlMasterDataSource, string> = {
  manual: 'manuell',
  wilken: 'Wilken',
  maas: 'maas',
  import: 'Import',
};

export const SOURCE_STYLE: Record<ControlMasterDataSource, string> = {
  manual: 'bg-gray-100 text-gray-600 dark:bg-primary-600 dark:text-primary-200',
  wilken: 'bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
  maas: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  import: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
};

export const STEUERPFAD_LABELS: Record<Steuerpfad, string> = {
  relay: 'Relais (W4)',
  eebus: 'EEBUS',
  ems: 'Energiemanagementsystem',
  unknown: 'unbekannt',
};

export const BOX_TYPE_LABELS: Record<SteuerboxType, { title: string; subtitle: string; dot: string }> = {
  analog: { title: 'FNN Steuerbox', subtitle: 'FNN', dot: 'bg-[#4285f4]' },
  digital: { title: 'Energiemanager', subtitle: 'Energiemanager', dot: 'bg-[#34a853]' },
  hybrid: { title: 'FNN Steuerbox (hybrid)', subtitle: 'Relais + digital', dot: 'bg-[#a142f4]' },
};

export const CODING_LABELS: Record<string, string> = {
  binary: 'Relaisstufen',
  fixed: 'feste Stufen',
  eebus: 'EEBUS',
  unknown: 'Kodierung unbekannt',
};

/** Rows of an external source (sync) versus rows the operator owns. */
export const isSynced = (source: ControlMasterDataSource | undefined | null): boolean =>
  source != null && source !== 'manual';

/**
 * A calendar date from the backend ("2026-03-05") as "05.03.2026". Deliberately string-based: it is a
 * source wall-clock date, not an instant, and must never be shifted by a time zone.
 */
export const formatCalendarDate = (value?: string | null): string | null => {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : value;
};

/** Watt (master data unit) as kW text. */
export const formatWattAsKw = (w?: number | null): string | null => (w == null ? null : `${fmt(w / 1000, 1)} kW`);

/** §14a status of a resource as text: registered, not registered, or unknown (type rule applies). */
export const participationLabel = (tr: TechnischeRessource): string =>
  tr.participates14a === true ? '§ 14a angemeldet' : tr.participates14a === false ? 'nicht § 14a' : '§ 14a unbekannt';

/** An installed box is one without removal date. */
export const isInstalled = (box: Steuerbox): boolean => !box.removedAt;
